import { Grid, Paper, Button, Box, CircularProgress } from '@mui/material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getDonorById } from 'src/shared/services/donorService';
import { Edit } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { DonorResponse } from '../models/donor';
import { DonorDetailInfo } from '../components/donorDetailInfo';
import { DonorDetailSubscriptions } from '../components/donorDetailSubscriptions';
import { DonorEditModal } from '../components/donorEditModal';

type DonorsDetailParams = {
  id: string;
};

export const DonorsDetailPage = () => {
  const params = useParams<DonorsDetailParams>();
  const isMounted = useIsMounted();
  const { dateStamp, setDateStamp } = useGlobals();
  const [donor, setDonor] = useState<DonorResponse>();
  const [loading, setLoading] = useState(true);
  const [editDonor, setEditDonor] = useState(false);

  const { t } = useTranslation(['gift']);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDonorById(params.id ?? '');
      if (isMounted()) {
        ConsoleHelper.log('DonorsDetailPage', result.data, dateStamp);
        setDonor(result.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [params.id, isMounted, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} lg={8}>
          <MainContentHeader title={t('gift:donors')} isLoading={loading} />
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && donor && (
              <GridWrapper>
                <Grid item xs={12}>
                  <ContentSection>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => setEditDonor(true)}
                      endIcon={<Edit />}
                      sx={{ position: 'absolute', top: '1rem', right: '1rem' }}
                      disabled={donor.id === null || loading}
                    >
                      {t('common:button.edit')}
                    </Button>
                    <DonorDetailInfo donor={donor} />
                  </ContentSection>
                </Grid>
                <Grid item xs={12}>
                  <DonorDetailSubscriptions donorId={donor.id} subscriptions={donor.subscriptions ?? []} />
                </Grid>
              </GridWrapper>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {donor && editDonor && (
        <DonorEditModal
          donor={donor}
          onClose={() => {
            setEditDonor(false);
          }}
          onSave={() => {
            setEditDonor(false);
            setDateStamp(new Date());
          }}
          modalOpen={editDonor}
        />
      )}
    </>
  );
};
